'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { useContactModal } from '../../../contexts/ContactModalContext';

import {
    ArrowRight,
    Eye,
    Rocket,
    Users,
    Compass,
    Heart,
} from 'lucide-react';

export default function AboutPage() {
    const [activeStep, setActiveStep] = useState(0);
    const { openModal } = useContactModal();

    // Animation presets
    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] } }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15
            }
        }
    };

    const team = [
        {
            name: "Sukriti Goyal",
            role: "Co-Founder & CEO",
            image: "/images/about/team-sukriti_founder.webp",
            bio: "Leading the creative direction and strategic positioning at Lead Workz with a deep foundation in audience psychology and graphic storytelling.",
        },
        {
            name: "Vartika Goyal",
            role: "CFO",
            image: "/images/about/vartika_goyal.webp",
            bio: "Driving financial strategy and operational excellence to ensure sustainable growth and long-term value creation.",
        },
        {
            name: "Lekhana Gowda",
            role: "UI/UX Designer",
            image: "/images/about/team-lekhana.webp",
            bio: "Crafting beautiful digital journeys by blending deep user research with cutting-edge visual design.",
            objectPos: "object-[center_20%]",
            scale: "scale-180",
        },
        {
            name: "Kamalnath",
            role: "Full Stack Developer",
            image: "/images/about/team-kamal.webp",
            bio: "Translating sophisticated strategic visions and designs into highly performant, clean production code.",
            objectFit: "object-cover",
            objectPos: "object-[center_65%]",
            scale: "scale-150",
        },
        {
            name: "Sumit Gupta",
            role: "Graphic Designer & Video Editor",
            image: "/images/about/team-sumith.webp",
            bio: "Creating strong brand identity languages and memorable visual assets that stand out in crowded feeds.",
        },
        {
            name: "Nabeel",
            role: "Digital Marketing Executive",
            image: "/images/about/team-nabeel.webp",
            bio: "Structuring data-aligned performance marketing systems to generate sustainable compound growth.",
            social: { linkedin: "#", twitter: "#", portfolio: "#" }
        },
        {
            name: "Sneha",
            role: "Social Media & Content Executive",
            image: "/images/about/team-sneha.webp",
            bio: "Shaping the brand's voice across social platforms with content that connects, engages and builds community.",
            social: { linkedin: "#", twitter: "#", portfolio: "#" }
        }
    ];

    return (
        <main className="about-root relative min-h-screen bg-black text-white overflow-hidden">
            {/* Background ambient glows */}
            <div className="absolute top-[-10%] left-[-10%] w-[50%] aspect-square rounded-full bg-violet-600/10 blur-[120px] pointer-events-none" />
            <div className="absolute top-[30%] right-[-10%] w-[45%] aspect-square rounded-full bg-blue-600/10 blur-[130px] pointer-events-none" />
            <div className="absolute bottom-[10%] left-[20%] w-[40%] aspect-square rounded-full bg-purple-700/10 blur-[120px] pointer-events-none" />

            {/* ═══ 1. HERO SECTION ═══ */}
            <section className="relative w-full md:h-[60vh] md:min-h-[640px] overflow-hidden">
                {/* Video — full background on mobile, right-side on desktop */}
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover object-center md:object-contain md:object-right md:inset-auto md:top-0 md:right-0 md:h-full md:w-[65%]"
                >
                    <source src="/videos/prism-hero.mp4" type="video/mp4" />
                </video>
                {/* Dark overlay — stronger on mobile for readability */}
                <div className="absolute inset-0 pointer-events-none md:hidden" style={{ background: 'rgba(0,0,0,0.65)' }} />
                <div className="absolute inset-0 pointer-events-none hidden md:block" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.97) 0%, rgba(0,0,0,0.9) 30%, rgba(0,0,0,0.5) 48%, rgba(0,0,0,0.05) 62%, transparent 75%)' }} />
                {/* Mobile bottom fade */}
                <div className="absolute bottom-0 left-0 right-0 h-20 md:hidden pointer-events-none" style={{ background: 'linear-gradient(to top, #000 0%, transparent 100%)' }} />

                {/* Text — desktop: absolute centered; mobile: relative with padding */}
                <motion.div
                    initial="hidden" animate="visible" variants={staggerContainer}
                    className="relative md:absolute md:top-56 md:left-[5%] z-10 flex flex-col gap-4 md:gap-6 max-w-md md:max-w-xl px-6 md:px-0 pt-24 md:pt-0 pb-8 md:pb-0"
                >
                    <motion.h1 variants={fadeInUp} className="text-3xl md:text-6xl font-extrabold tracking-tight leading-[1.05] font-clash text-white whitespace-nowrap">
                       The{' '}
                        <span style={{ color: '#CEFF1A' }}>Art</span> of Influence
                    </motion.h1>

                    <motion.p variants={fadeInUp} className="text-sm md:text-lg text-slate-300 leading-relaxed font-manrope">
Every purchase ever made was an emotion wearing a price tag.People don't choose products or services — they choose the version of themselves those make possible. They buy belonging. They invest in relief. The story a brand tells isn't decoration — it's the decision.
                    </motion.p>

                    <motion.div variants={fadeInUp}>
                        <button onClick={openModal} className="group inline-flex items-center gap-2 px-7 py-3 md:px-8 md:py-4 bg-[#ceff1a] text-black font-semibold rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(206,255,26,0.3)] font-clash">
                            <span>Start a Project</span>
                            <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                    </motion.div>
                </motion.div>
            </section>

            {/* ═══ 2. COMPARISON SECTION (A Different Way of Seeing Marketing) ═══ */}
            <section className="relative py-10 md:py-20 px-6 max-w-7xl mx-auto z-10">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={staggerContainer}
                    className="flex flex-col gap-12"
                >
                    <motion.div variants={fadeInUp} className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="max-w-xl">
                            <h2 className="text-3xl md:text-4xl font-bold font-clash tracking-tight">
                                A different way of seeing marketing
                            </h2>
                            <p className="text-slate-400 mt-2 font-manrope">
                                A new perspective rooted in empathy, not exposure
                            </p>
                        </div>
                        <div className="h-[2px] w-20 bg-[#ceff1a] hidden md:block" />
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-4">
                        {/* Old Lens Card */}
                        <motion.div
                            variants={fadeInUp}
                            whileHover={{ y: -8, transition: { duration: 0.3 } }}
                            className="group relative bg-zinc-900/40 border border-white/5 rounded-[2.5rem] p-8 md:p-12 overflow-hidden backdrop-blur-xl"
                        >
                            <div className="absolute top-0 right-0 w-24 h-24 bg-red-500/5 rounded-full blur-2xl group-hover:bg-red-500/10 transition-colors duration-500" />
                            <div className="flex flex-col gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full border border-red-500/20 flex items-center justify-center text-red-500 bg-red-500/5">
                                        <Eye className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-xl font-bold font-clash tracking-wider uppercase text-red-400">The Old Lens</h3>
                                </div>
                                <p className="text-slate-400 leading-relaxed font-manrope text-lg">
                                    Marketing viewed merely as campaigns, trends, and superficial formats — chasing the next short-term wave, built strictly to get seen.
                                </p>
                            </div>
                        </motion.div>

                        {/* Our Lens Card */}
                        <motion.div
                            variants={fadeInUp}
                            whileHover={{ y: -8, transition: { duration: 0.3 } }}
                            className="group relative bg-gradient-to-b from-[#251e36] to-zinc-950/80 border border-violet-500/20 rounded-[2.5rem] p-8 md:p-12 overflow-hidden backdrop-blur-xl shadow-[0_10px_40px_-15px_rgba(139,92,246,0.15)]"
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 bg-violet-600/10 rounded-full blur-3xl group-hover:bg-violet-600/20 transition-colors duration-500" />
                            <div className="flex flex-col gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full border border-violet-500/30 flex items-center justify-center text-violet-400 bg-violet-500/10">
                                        <Heart className="w-5 h-5 text-[#ceff1a]" />
                                    </div>
                                    <h3 className="text-xl font-bold font-clash tracking-wider uppercase text-violet-300">Our Lens</h3>
                                </div>
                                <p className="text-slate-200 leading-relaxed font-manrope text-lg">
                                    Marketing as pure empathy — understanding your audience's deep inner world, and building genuine bridges to who they want to become.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </section>

            {/* ═══ 3. QUOTE / OVAL IMAGE SECTION ═══ */}
            <section className="relative py-10 md:py-20 px-6 max-w-7xl mx-auto z-10">
                <motion.div
                    initial={{ opacity: 0, scale: 0.97 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="relative w-full"
                    style={{ padding: '40px' }}
                >
                    {/* Ring — single oval */}
                    <div className="absolute pointer-events-none" style={{ inset: 0, border: '2px solid rgba(255,255,255,0.45)', borderRadius: '9999px' }} />

                    {/* Inner content row */}
                    <div className="relative flex items-center justify-center">
                        {/* Statement Block */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="flex flex-col gap-6 text-center px-8"
                        >
                            <span className="text-[#ceff1a] text-sm font-semibold uppercase tracking-widest font-clash">Core Philosophy</span>
                            <blockquote className="text-1xl md:text-4xl font-extrabold italic leading-tight font-clash text-transparent bg-clip-text bg-gradient-to-br from-white to-slate-400">
                                "We treat marketing less like a broadcast and more like a conversation with someone's inner world."
                            </blockquote>
                        </motion.div>
                    </div>
                </motion.div>
            </section>

            {/* ═══ 4. HOW WE WORK (Pillars & Process) ═══ */}
            <section className="relative py-10 md:py-20 px-6 max-w-7xl mx-auto z-10">
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={staggerContainer}
                    className="flex flex-col gap-16"
                >
                    <motion.div variants={fadeInUp} className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="max-w-xl">
                            <h2 className="text-3xl md:text-4xl font-bold font-clash tracking-tight">
                                How we actually work
                            </h2>
                            <p className="text-slate-400 mt-2 font-manrope">
                                Inside the process that drives every result we deliver
                            </p>
                        </div>
                        <div className="h-[2px] w-20 bg-[#ceff1a] hidden md:block" />
                    </motion.div>

                    {/* Mobile: tap-to-expand accordion — same visual as desktop */}
                    <div className="md:hidden flex flex-col gap-3">
                        {[
                            {
                                num: '01', label: 'Deep Immersion', subtitle: 'Start with total understanding.', description: "We live inside your brand before we build for it. We carry your ambitions, sit with your audience's hesitations, and study what makes your industry's psychology unlike any other.",
                                illustration: (
                                    <svg width="120" height="100" viewBox="0 0 120 100" fill="none"><ellipse cx="60" cy="50" rx="52" ry="28" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.5"/><ellipse cx="60" cy="50" rx="36" ry="18" stroke="#CEFF1A" strokeWidth="1" opacity="0.3"/><circle cx="60" cy="50" r="16" fill="rgba(206,255,26,0.1)" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.6"/><circle cx="60" cy="50" r="7" fill="#CEFF1A" opacity="0.8"/><circle cx="60" cy="50" r="3" fill="#fff"/><circle cx="66" cy="45" r="2" fill="#fff" opacity="0.5"/></svg>
                                )
                            },
                            {
                                num: '02', label: 'Uncopyable Strategy', subtitle: "Build what competitors can't copy.", description: "We translate what we've learned into a customized marketing blueprint shaped entirely by your unique DNA — literally impossible for competitors to replicate.",
                                illustration: (
                                    <svg width="120" height="100" viewBox="0 0 120 100" fill="none"><path d="M60 8 C60 8 44 22 44 40 C44 56 52 66 60 76 C68 66 76 56 76 40 C76 22 60 8 60 8Z" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.5" fill="rgba(206,255,26,0.04)"/><circle cx="60" cy="42" r="10" fill="rgba(206,255,26,0.15)" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.7"/><circle cx="60" cy="42" r="4" fill="#CEFF1A" opacity="0.9"/><line x1="30" y1="82" x2="90" y2="82" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.4"/><line x1="38" y1="90" x2="82" y2="90" stroke="#CEFF1A" strokeWidth="1" opacity="0.25"/></svg>
                                )
                            },
                            {
                                num: '03', label: 'Precision Execution', subtitle: 'Compound budget on proven, profitable structure.', description: 'We execute with intense discipline, measuring what actually drives bottom-line growth. We iterate rapidly on performance data, ignoring vanity metrics.',
                                illustration: (
                                    <svg width="120" height="100" viewBox="0 0 120 100" fill="none"><circle cx="60" cy="50" r="44" stroke="#CEFF1A" strokeWidth="1" opacity="0.15"/><circle cx="60" cy="50" r="32" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.3"/><circle cx="60" cy="50" r="20" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.5"/><circle cx="60" cy="50" r="10" stroke="#CEFF1A" strokeWidth="2" opacity="0.7"/><circle cx="60" cy="50" r="4" fill="#CEFF1A"/><line x1="60" y1="6" x2="60" y2="18" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/><line x1="60" y1="82" x2="60" y2="94" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/><line x1="16" y1="50" x2="28" y2="50" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/><line x1="92" y1="50" x2="104" y2="50" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/></svg>
                                )
                            },
                        ].map((step, i) => (
                            <div
                                key={step.num}
                                onClick={() => setActiveStep(activeStep === i ? -1 : i)}
                                className="relative overflow-hidden rounded-[2rem] cursor-pointer bg-zinc-900/70 border border-white/[0.06] backdrop-blur-sm transition-all duration-500"
                                style={{ height: activeStep === i ? '220px' : '72px' }}
                            >
                                {/* Large ghost number */}
                                <span
                                    className="absolute font-extrabold font-clash select-none pointer-events-none leading-none"
                                    style={{
                                        fontSize: '180px',
                                        top: '-20px',
                                        left: '12px',
                                        background: 'linear-gradient(175deg, rgba(206,255,26,0.72) 0%, rgba(160,200,10,0.45) 55%, rgba(0,0,0,0.15) 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {step.num}
                                </span>

                                {/* Collapsed: number + label + tap hint */}
                                <div
                                    className="absolute inset-0 flex items-center px-6 gap-4"
                                    style={{ opacity: activeStep === i ? 0 : 1, transition: 'opacity 0.2s ease', pointerEvents: activeStep === i ? 'none' : 'auto' }}
                                >
                                    <span className="text-[#ceff1a] text-xl font-black font-clash tracking-tight shrink-0">{step.num}</span>
                                    <span className="text-white text-sm font-semibold font-clash tracking-wide">{step.label}</span>
                                    <span className="ml-auto text-white/30 text-xs font-clash shrink-0">tap +</span>
                                </div>

                                {/* Illustration top-right */}
                                <div className="absolute top-2 right-4 pointer-events-none" style={{ opacity: activeStep === i ? 0.7 : 0, transition: 'opacity 0.3s ease 0.1s' }}>
                                    {step.illustration}
                                </div>
                                {/* Expanded: content at bottom */}
                                <div
                                    className="absolute bottom-0 left-0 right-0 p-6 flex flex-col gap-1"
                                    style={{ opacity: activeStep === i ? 1 : 0, transform: activeStep === i ? 'translateY(0)' : 'translateY(12px)', transition: 'opacity 0.3s ease 0.15s, transform 0.3s ease 0.15s' }}
                                >
                                    <p className="text-slate-400 text-xs font-manrope">{step.subtitle}</p>
                                    <h3 className="text-xl font-bold font-clash text-white">{step.label}</h3>
                                    <p className="text-slate-500 text-xs font-manrope leading-relaxed mt-0.5 line-clamp-3">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop: expand-on-hover accordion */}
                    <div
                        className="hidden md:flex gap-3 h-[400px]"
                        onMouseLeave={() => setActiveStep(0)}
                    >
                        {[
                            {
                                num: '01', label: 'Deep Immersion', subtitle: 'Start with total understanding.', description: "We live inside your brand before we build for it. We carry your ambitions, sit with your audience's hesitations, and study what makes your industry's psychology unlike any other.",
                                illustration: (
                                    <svg width="360" height="320" viewBox="0 0 360 320" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        {/* Eye outline */}
                                        <path d="M20 160 C80 60 280 60 340 160 C280 260 80 260 20 160Z" stroke="#CEFF1A" strokeWidth="1.5" fill="rgba(206,255,26,0.03)" opacity="0.5">
                                            <animate attributeName="opacity" values="0.5;0.8;0.5" dur="3s" repeatCount="indefinite"/>
                                        </path>
                                        <path d="M60 160 C100 100 260 100 300 160 C260 220 100 220 60 160Z" stroke="#CEFF1A" strokeWidth="1" fill="none" opacity="0.25"/>
                                        {/* Rotating dashed iris ring */}
                                        <g style={{transformOrigin:'180px 160px'}}>
                                            <animateTransform attributeName="transform" type="rotate" from="0 180 160" to="360 180 160" dur="8s" repeatCount="indefinite"/>
                                            <circle cx="180" cy="160" r="72" stroke="#CEFF1A" strokeWidth="1" strokeDasharray="6 4" opacity="0.35" fill="none"/>
                                        </g>
                                        {/* Counter-rotating iris ring */}
                                        <g>
                                            <animateTransform attributeName="transform" type="rotate" from="0 180 160" to="-360 180 160" dur="12s" repeatCount="indefinite"/>
                                            <circle cx="180" cy="160" r="52" stroke="#CEFF1A" strokeWidth="1.5" strokeDasharray="10 6" opacity="0.4" fill="none"/>
                                        </g>
                                        <circle cx="180" cy="160" r="34" stroke="#CEFF1A" strokeWidth="2" opacity="0.55" fill="rgba(206,255,26,0.05)"/>
                                        {/* Pulsing pupil */}
                                        <circle cx="180" cy="160" r="18" fill="rgba(206,255,26,0.15)" stroke="#CEFF1A" strokeWidth="2" opacity="0.8">
                                            <animate attributeName="r" values="18;22;18" dur="2.5s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.8;1;0.8" dur="2.5s" repeatCount="indefinite"/>
                                        </circle>
                                        <circle cx="180" cy="160" r="8" fill="#CEFF1A">
                                            <animate attributeName="r" values="8;10;8" dur="2.5s" repeatCount="indefinite"/>
                                        </circle>
                                        <circle cx="186" cy="154" r="3.5" fill="white" opacity="0.75"/>
                                        {/* Scanning line sweeping across eye */}
                                        <line x1="40" y1="160" x2="320" y2="160" stroke="#CEFF1A" strokeWidth="1" opacity="0">
                                            <animate attributeName="y1" values="110;210;110" dur="3s" repeatCount="indefinite"/>
                                            <animate attributeName="y2" values="110;210;110" dur="3s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0;0.35;0" dur="3s" repeatCount="indefinite"/>
                                        </line>
                                        {/* Corner brackets */}
                                        <path d="M20 130 L20 110 L40 110" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.4" fill="none"/>
                                        <path d="M340 130 L340 110 L320 110" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.4" fill="none"/>
                                        <path d="M20 190 L20 210 L40 210" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.4" fill="none"/>
                                        <path d="M340 190 L340 210 L320 210" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.4" fill="none"/>
                                        {/* Tick marks */}
                                        <line x1="180" y1="88" x2="180" y2="104" stroke="#CEFF1A" strokeWidth="1" opacity="0.4"/>
                                        <line x1="180" y1="216" x2="180" y2="232" stroke="#CEFF1A" strokeWidth="1" opacity="0.4"/>
                                        <line x1="100" y1="160" x2="116" y2="160" stroke="#CEFF1A" strokeWidth="1" opacity="0.4"/>
                                        <line x1="244" y1="160" x2="260" y2="160" stroke="#CEFF1A" strokeWidth="1" opacity="0.4"/>
                                    </svg>
                                )
                            },
                            {
                                num: '02', label: 'Uncopyable Strategy', subtitle: "Build what competitors can't copy.", description: "We translate what we've learned into a customized marketing blueprint shaped entirely by your unique DNA — literally impossible for competitors to replicate.",
                                illustration: (
                                    <svg width="360" height="320" viewBox="0 0 360 320" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        {/* Blueprint grid */}
                                        {[40,80,120,160,200,240,280,320].map(x => <line key={`v${x}`} x1={x} y1="20" x2={x} y2="300" stroke="#CEFF1A" strokeWidth="0.4" opacity="0.07"/>)}
                                        {[40,80,120,160,200,240,280].map(y => <line key={`h${y}`} x1="20" y1={y} x2="340" y2={y} stroke="#CEFF1A" strokeWidth="0.4" opacity="0.07"/>)}
                                        {/* DNA helix — animating with scrolling offset */}
                                        <g>
                                            <animateTransform attributeName="transform" type="translate" values="0,0;0,-30;0,0" dur="4s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.6 1;0.4 0 0.6 1"/>
                                            {/* Left strand */}
                                            <path d="M180 0 C140 30 140 70 180 100 C220 130 220 170 180 200 C140 230 140 270 180 300 C220 330 220 370 180 400" stroke="#CEFF1A" strokeWidth="2.5" opacity="0.7" fill="none" strokeLinecap="round"/>
                                            {/* Right strand */}
                                            <path d="M180 0 C220 30 220 70 180 100 C140 130 140 170 180 200 C220 230 220 270 180 300 C140 330 140 370 180 400" stroke="#CEFF1A" strokeWidth="2.5" opacity="0.35" fill="none" strokeLinecap="round"/>
                                            {/* Rungs */}
                                            {[35,65,95,135,165,200,235,265,295,325,355,385].map((y, j) => (
                                                <g key={j}>
                                                    <line x1="152" y1={y} x2="208" y2={y} stroke="#CEFF1A" strokeWidth="1.5" opacity={j%2===0?0.45:0.3}/>
                                                    <circle cx={j%2===0?152:208} cy={y} r="5" fill="#CEFF1A" opacity="0.85">
                                                        <animate attributeName="opacity" values="0.85;0.4;0.85" dur="2s" begin={`${j*0.25}s`} repeatCount="indefinite"/>
                                                    </circle>
                                                    <circle cx={j%2===0?208:152} cy={y} r="5" fill="#CEFF1A" opacity="0.4"/>
                                                </g>
                                            ))}
                                        </g>
                                        {/* Lock icon — pulsing */}
                                        <rect x="288" y="48" width="44" height="34" rx="7" stroke="#CEFF1A" strokeWidth="1.5" fill="rgba(206,255,26,0.05)">
                                            <animate attributeName="opacity" values="1;0.4;1" dur="3s" repeatCount="indefinite"/>
                                        </rect>
                                        <path d="M299 48 C299 35 321 35 321 48" stroke="#CEFF1A" strokeWidth="1.5" fill="none" opacity="0.6"/>
                                        <circle cx="310" cy="65" r="5" fill="#CEFF1A" opacity="0.7">
                                            <animate attributeName="r" values="5;7;5" dur="3s" repeatCount="indefinite"/>
                                        </circle>
                                        <line x1="310" y1="70" x2="310" y2="77" stroke="#CEFF1A" strokeWidth="2" opacity="0.6"/>
                                        {/* Fingerprint arcs */}
                                        <path d="M88 200 C88 150 128 115 180 115 C232 115 272 150 272 200" stroke="#CEFF1A" strokeWidth="1" strokeDasharray="5 4" opacity="0.18" fill="none"/>
                                        <path d="M104 200 C104 158 138 128 180 128 C222 128 256 158 256 200" stroke="#CEFF1A" strokeWidth="1" strokeDasharray="5 4" opacity="0.12" fill="none"/>
                                    </svg>
                                )
                            },
                            {
                                num: '03', label: 'Precision Execution', subtitle: 'Compound budget on proven, profitable structure.', description: 'We execute with intense discipline, measuring what actually drives bottom-line growth. We iterate rapidly on performance data, ignoring vanity metrics.',
                                illustration: (
                                    <svg width="360" height="320" viewBox="0 0 360 320" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        {/* Rippling target rings */}
                                        <circle cx="195" cy="155" r="130" stroke="#CEFF1A" strokeWidth="0.8" opacity="0.1">
                                            <animate attributeName="r" values="130;138;130" dur="3s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.1;0.02;0.1" dur="3s" repeatCount="indefinite"/>
                                        </circle>
                                        <circle cx="195" cy="155" r="100" stroke="#CEFF1A" strokeWidth="1" opacity="0.18">
                                            <animate attributeName="r" values="100;108;100" dur="3s" begin="0.3s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.18;0.06;0.18" dur="3s" begin="0.3s" repeatCount="indefinite"/>
                                        </circle>
                                        <circle cx="195" cy="155" r="72" stroke="#CEFF1A" strokeWidth="1.5" opacity="0.28">
                                            <animate attributeName="r" values="72;78;72" dur="3s" begin="0.6s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.28;0.12;0.28" dur="3s" begin="0.6s" repeatCount="indefinite"/>
                                        </circle>
                                        <circle cx="195" cy="155" r="48" stroke="#CEFF1A" strokeWidth="2" opacity="0.45"/>
                                        <circle cx="195" cy="155" r="28" stroke="#CEFF1A" strokeWidth="2" opacity="0.65" fill="rgba(206,255,26,0.07)"/>
                                        <circle cx="195" cy="155" r="12" fill="rgba(206,255,26,0.25)" stroke="#CEFF1A" strokeWidth="2" opacity="0.9">
                                            <animate attributeName="r" values="12;15;12" dur="2s" repeatCount="indefinite"/>
                                        </circle>
                                        <circle cx="195" cy="155" r="5" fill="#CEFF1A">
                                            <animate attributeName="opacity" values="1;0.5;1" dur="1.5s" repeatCount="indefinite"/>
                                        </circle>
                                        {/* Crosshairs */}
                                        <line x1="195" y1="20" x2="195" y2="62" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/>
                                        <line x1="195" y1="248" x2="195" y2="290" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/>
                                        <line x1="35" y1="155" x2="82" y2="155" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/>
                                        <line x1="308" y1="155" x2="355" y2="155" stroke="#CEFF1A" strokeWidth="2" opacity="0.5"/>
                                        {/* Arrow flying in */}
                                        <g>
                                            <animateTransform attributeName="transform" type="translate" values="-30,-30;0,0;0,0" dur="2s" repeatCount="indefinite" calcMode="spline" keySplines="0.4 0 0.2 1;0.4 0 0.2 1" keyTimes="0;0.4;1"/>
                                            <line x1="75" y1="75" x2="188" y2="148" stroke="#CEFF1A" strokeWidth="2" opacity="0.65"/>
                                            <polygon points="188,148 176,137 190,135" fill="#CEFF1A" opacity="0.9"/>
                                        </g>
                                        {/* Animated bar chart */}
                                        <rect x="22" y="290" width="14" height="0" rx="3" fill="#CEFF1A" opacity="0.25">
                                            <animate attributeName="height" values="0;60;60" dur="2s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                            <animate attributeName="y" values="290;230;230" dur="2s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                        </rect>
                                        <rect x="42" y="290" width="14" height="0" rx="3" fill="#CEFF1A" opacity="0.38">
                                            <animate attributeName="height" values="0;80;80" dur="2s" begin="0.15s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                            <animate attributeName="y" values="290;210;210" dur="2s" begin="0.15s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                        </rect>
                                        <rect x="62" y="290" width="14" height="0" rx="3" fill="#CEFF1A" opacity="0.55">
                                            <animate attributeName="height" values="0;100;100" dur="2s" begin="0.3s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                            <animate attributeName="y" values="290;190;190" dur="2s" begin="0.3s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                        </rect>
                                        <rect x="82" y="290" width="14" height="0" rx="3" fill="#CEFF1A" opacity="0.75">
                                            <animate attributeName="height" values="0;120;120" dur="2s" begin="0.45s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                            <animate attributeName="y" values="290;170;170" dur="2s" begin="0.45s" repeatCount="indefinite" keyTimes="0;0.4;1"/>
                                        </rect>
                                        <line x1="18" y1="293" x2="102" y2="293" stroke="#CEFF1A" strokeWidth="1" opacity="0.4"/>
                                        {/* Data ping dots */}
                                        <circle cx="265" cy="78" r="5" fill="#CEFF1A" opacity="0.7"/>
                                        <circle cx="265" cy="78" r="5" fill="none" stroke="#CEFF1A" strokeWidth="1.5" opacity="0">
                                            <animate attributeName="r" values="5;22;5" dur="2s" repeatCount="indefinite"/>
                                            <animate attributeName="opacity" values="0.6;0;0.6" dur="2s" repeatCount="indefinite"/>
                                        </circle>
                                    </svg>
                                )
                            },
                        ].map((step, i) => (
                            <div
                                key={step.num}
                                onMouseEnter={() => setActiveStep(i)}
                                className="relative overflow-hidden rounded-[2rem] cursor-pointer bg-zinc-900/70 border border-white/6 backdrop-blur-sm"
                                style={{
                                    flexBasis: activeStep === i ? '0%' : '72px',
                                    flexGrow: activeStep === i ? 1 : 0,
                                    flexShrink: 0,
                                    minWidth: '72px',
                                    transition: 'flex-grow 0.55s cubic-bezier(0.4,0,0.2,1), flex-basis 0.55s cubic-bezier(0.4,0,0.2,1)',
                                }}
                            >
                                <span className="absolute font-extrabold font-clash select-none pointer-events-none leading-none"
                                    style={{ fontSize: '220px', top: '-20px', left: '12px', background: 'linear-gradient(175deg, rgba(206,255,26,0.72) 0%, rgba(160,200,10,0.45) 55%, rgba(0,0,0,0.15) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', whiteSpace: 'nowrap' }}>
                                    {step.num}
                                </span>
                                <div className="absolute inset-0 flex items-center justify-center" style={{ opacity: activeStep === i ? 0 : 1, transition: 'opacity 0.2s ease', pointerEvents: activeStep === i ? 'none' : 'auto' }}>
                                    <span className="text-white text-sm font-semibold font-clash whitespace-nowrap tracking-wide" style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)' }}>{step.label}</span>
                                </div>
                                {/* Illustration — fills right half of card */}
                                <div className="absolute top-0 right-0 bottom-0 flex items-center justify-center pointer-events-none" style={{ width: '55%', opacity: activeStep === i ? 1 : 0, transition: 'opacity 0.5s ease 0.2s' }}>
                                    {step.illustration}
                                </div>
                                {/* Bottom text */}
                                <div className="absolute bottom-0 left-0 right-0 p-8 flex flex-col gap-2" style={{ opacity: activeStep === i ? 1 : 0, transform: activeStep === i ? 'translateY(0)' : 'translateY(12px)', transition: 'opacity 0.3s ease 0.18s, transform 0.3s ease 0.18s' }}>
                                    <p className="text-slate-400 text-sm font-manrope">{step.subtitle}</p>
                                    <h3 className="text-2xl font-bold font-clash text-white">{step.label}</h3>
                                    <p className="text-slate-500 text-sm font-manrope leading-relaxed mt-1 max-w-[45%]">{step.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </section>

            {/* ═══ 5. VISION & MISSION SECTION ═══ */}
            <section className="relative py-10 md:py-20 px-6 max-w-7xl mx-auto z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Vision Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        whileHover={{ scale: 1.01 }}
                        className="relative bg-gradient-to-br from-[#120f26] via-zinc-950 to-zinc-950 border border-violet-500/10 rounded-[3rem] p-8 md:p-12 overflow-hidden shadow-xl"
                    >
                        <div className="absolute top-0 right-0 w-32 h-32 bg-violet-600/5 rounded-full blur-3xl pointer-events-none" />
                        <div className="flex flex-col gap-6">
                            <div className="w-16 h-16 rounded-2xl border border-violet-500/20 bg-violet-500/5 flex items-center justify-center text-violet-400">
                                <Compass className="w-8 h-8 text-[#ceff1a]" />
                            </div>
                            <span className="text-[#ceff1a] text-sm uppercase tracking-widest font-semibold font-clash">Our Vision</span>
                            <h3 className="text-3xl font-extrabold font-clash tracking-tight">Enduring Digital Brands.</h3>
                            <p className="text-slate-400 font-manrope leading-relaxed text-lg">
                                To build enduring digital brands rooted in clarity, credibility, and measurable impact — where visibility is intentional, engagement is meaningful, and growth is structured for long-term relevance in an evolving digital world.
                            </p>
                        </div>
                    </motion.div>

                    {/* Mission Card */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        whileHover={{ scale: 1.01 }}
                        className="relative bg-gradient-to-br from-[#0c1b30] via-zinc-950 to-zinc-950 border border-blue-500/10 rounded-[3rem] p-8 md:p-12 overflow-hidden shadow-xl"
                    >
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 rounded-full blur-3xl pointer-events-none" />
                        <div className="flex flex-col gap-6">
                            <div className="w-16 h-16 rounded-2xl border border-blue-500/20 bg-blue-500/5 flex items-center justify-center text-blue-400">
                                <Rocket className="w-8 h-8 text-[#ceff1a]" />
                            </div>
                            <span className="text-[#ceff1a] text-sm uppercase tracking-widest font-semibold font-clash">Our Mission</span>
                            <h3 className="text-3xl font-extrabold font-clash tracking-tight">Data-Aligned Ecosystems.</h3>
                            <p className="text-slate-400 font-manrope leading-relaxed text-lg">
                                To design transparent, data-aligned marketing ecosystems that strengthen brand presence, elevate user experience, and create consistent, performance-backed growth through discipline, insight, and strategic execution.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ═══ LIGHT THEME SECTION (TEAM & CTA) ═══ */}
            {/* ═══ LIGHT THEME SECTION (TEAM & CTA) ═══ */}
            <div className="relative z-20 -mt-2 overflow-hidden">

                {/* TOP CURVED TRANSITION */}
                <div className="absolute top-0 left-0 w-full h-[120px] bg-white rounded-t-[60px] z-0" />

                {/* MAIN WHITE CONTAINER */}
                <div className="relative bg-white text-zinc-900 pt-24 pb-16 rounded-t-[60px] rounded-b-[60px] overflow-hidden z-10">

                    {/* BACKGROUND GLOWS */}
                    <div className="absolute top-[-5%] left-[-5%] w-[40%] aspect-square rounded-full bg-violet-100/40 blur-[100px] pointer-events-none" />
                    <div className="absolute bottom-[5%] right-[-5%] w-[40%] aspect-square rounded-full bg-blue-50/50 blur-[100px] pointer-events-none" />

                    {/* ═══ 6. MEET OUR TEAM SECTION ═══ */}
                    <section className="relative max-w-7xl mx-auto px-6 z-10">
                        <motion.div
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={staggerContainer}
                            className="flex flex-col gap-16"
                        >
                            <motion.div variants={fadeInUp} className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div className="max-w-xl">
                                    <h2 className="text-3xl md:text-4xl font-bold font-clash tracking-tight text-zinc-950">
                                        Meet Our <span style={{ color: '#3730a3' }}>Team</span>
                                    </h2>
                                    <p className="text-zinc-500 mt-2 font-manrope text-base leading-relaxed">
                                        The people behind every strategy, campaign, and result we deliver
                                    </p>
                                </div>

                                <div className="h-[2px] w-20 bg-[#0033ff] hidden md:block" />
                            </motion.div>

                            {/* FEATURED FOUNDER */}
                            <motion.div
                                variants={fadeInUp}
                                className="relative bg-zinc-50 border border-zinc-200/80 rounded-[3rem] p-8 md:p-12 overflow-hidden shadow-lg shadow-zinc-100/50 backdrop-blur-xl"
                            >
                                <div className="absolute top-0 right-0 w-80 h-80 bg-violet-500/5 rounded-full blur-[100px] pointer-events-none" />

                                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

                                    {/* IMAGE */}
                                    <div className="lg:col-span-5 w-full aspect-square relative rounded-[2rem] overflow-hidden border border-zinc-200">
                                        <Image
                                            src="/images/about/preeti_goyal.webp"
                                            alt="Preeti Goyal Portrait"
                                            fill
                                            className="object-cover object-top"
                                            sizes="(max-width:768px) 100vw, 40vw"
                                        />
                                    </div>

                                    {/* CONTENT */}
                                    <div className="lg:col-span-7 flex flex-col gap-6">

                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2 px-3 py-1 bg-violet-50 border border-violet-100 rounded-full w-fit">
                                                <span className="text-xs uppercase tracking-widest font-semibold text-[#3730a3] font-clash">
                                                    Founder Spotlight
                                                </span>
                                            </div>

                                            <h3 className="text-3xl md:text-4xl font-extrabold font-clash text-zinc-900">
                                                Preeti Goyal
                                            </h3>

                                            <p className="text-[#3730a3] font-semibold text-lg font-clash">
                                                Co Founder
                                            </p>
                                        </div>

                                        <div className="h-[1px] w-full bg-zinc-200" />

                                        <p className="text-lg font-manrope leading-relaxed italic text-zinc-800">
                                            "People don't choose products or services — they choose the version of themselves those make possible."
                                        </p>

                                        <p className="text-zinc-650 font-manrope leading-relaxed">
                                            Lead Workz wasn't created to be another digital marketing agency. 
                                            It was built to bridge the gap between creative storytelling and measurable business growth.
                                             By combining branding, psychology, performance marketing, and data, the team helps 
                                            businesses build brands that people don't just discover—but genuinely trust.
                                        </p>

                                    </div>
                                </div>
                            </motion.div>

                            {/* TEAM GRID */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-4">

                                {team.map((member, i) => (
                                    <motion.div
                                        key={i}
                                        variants={fadeInUp}
                                        whileHover={{ y: -8 }}
                                        className="group bg-zinc-50/50 border border-zinc-200/60 rounded-[2rem] p-6 flex flex-col gap-6 shadow-sm shadow-zinc-100/50 hover:shadow-md hover:border-zinc-300 transition-all duration-300"
                                    >

                                        {/* IMAGE */}
                                        <div className="relative w-full aspect-square rounded-[1.5rem] overflow-hidden border border-zinc-200/60 shadow-inner">
                                            <Image
                                                src={member.image}
                                                alt={`${member.name} Photo`}
                                                fill
                                                className={`${(member as any).objectFit ?? 'object-cover'} ${(member as any).objectPos ?? 'object-center'} ${(member as any).scale ?? ''} transition-transform duration-500 group-hover:scale-105`}
                                                sizes="(max-width:768px) 100vw, 25vw"
                                            />

                                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                                        </div>

                                        {/* INFO */}
                                        <div className="flex flex-col gap-2">
                                            <h4 className="text-xl font-bold font-clash tracking-tight text-zinc-950">
                                                {member.name}
                                            </h4>

                                            <span className="text-[#3730a3] text-xs font-semibold uppercase tracking-wider font-clash">
                                                {member.role}
                                            </span>

                                            <p className="text-zinc-600 text-sm font-manrope leading-relaxed mt-2">
                                                {member.bio}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </section>

                    {/* ═══ CTA SECTION ═══ */}
                    <section className="relative pt-24 pb-8 px-6 max-w-7xl mx-auto z-10">

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="relative bg-gradient-to-br from-zinc-900 to-zinc-950 border border-zinc-800 rounded-[3rem] p-8 md:p-16 text-center overflow-hidden shadow-2xl text-white"
                        >

                            <div className="absolute inset-0 bg-violet-600/5 blur-3xl rounded-full scale-75 pointer-events-none" />

                            <div className="relative z-10 flex flex-col items-center gap-8 max-w-3xl mx-auto">

                                <div className="w-16 h-16 rounded-full bg-[#ceff1a]/10 flex items-center justify-center text-[#ceff1a] shadow-inner shadow-[#ceff1a]/20">
                                    <Users className="w-8 h-8" />
                                </div>

                                <h2 className="text-3xl md:text-5xl font-extrabold font-clash tracking-tight leading-tight">
                                    Ready to craft your uncopyable brand strategy?
                                </h2>

                                <p className="text-slate-300 text-base md:text-lg font-manrope leading-relaxed">
                                    That's what Lead Workz is built on — deep empathy, real storytelling,
                                    and the belief that when a brand is truly understood,
                                    the right strategy doesn't need to be forced.
                                </p>

                                <Link
                                    href="/contact"
                                    className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-black font-semibold rounded-full hover:bg-[#ceff1a] hover:scale-105 hover:shadow-[0_0_30px_rgba(206,255,26,0.2)] transition-all duration-300 font-clash"
                                >
                                    <span>Let's Build Together</span>

                                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                                </Link>

                            </div>
                        </motion.div>
                    </section>
                </div>
            </div>
        </main>
    );
}
